import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { UNAUTHENTICATED_PATHS } from '../../constants';
import { useInitializeRiskRegistryQuestions } from '../../contexts/RiskAssessmentQuestionsContext';
import { useSessionContext } from '../../contexts/SessionContext';
import { useAppContext } from '../../hooks/AppContext';
import {
  CheckRiskRegistryAnswerInput,
  Organization,
  QuestionType,
  useCreateActionItemRisksMutation,
  useCreateNavigationEventMutation,
} from '../../types/api.graphql';

export default function AuthenticatedRoute() {
  const { pathname, search } = useLocation();
  const { isAuthenticated } = useAppContext();
  const { activeDealerships, previousHelpNavigationPath, setPreviousPath } = useSessionContext();
  const previousLocation = useRef<string>(null);
  const [createNavigationEvent] = useCreateNavigationEventMutation();
  const { initializeRiskRegistryQuestions } = useInitializeRiskRegistryQuestions();
  const [createActionItemRisks] = useCreateActionItemRisksMutation();

  useEffect(() => {
    if (isAuthenticated) {
      const organizationQueryArguments: CheckRiskRegistryAnswerInput[] = activeDealerships.reduce(
        (accumulator: CheckRiskRegistryAnswerInput[], activeDealership: Organization) => {
          return [
            ...accumulator,
            {
              type:
                activeDealership.onboardingUntil &&
                !isNaN(new Date(activeDealership.onboardingUntil).getTime()) &&
                new Date(activeDealership.onboardingUntil) <= new Date()
                  ? QuestionType.Standard
                  : QuestionType.Onboarding,
              organizationId: activeDealership.id,
            },
          ];
        },
        [],
      );
      initializeRiskRegistryQuestions(organizationQueryArguments);

      activeDealerships.map(async (activeDealership) => {
        await createActionItemRisks({
          variables: {
            createActionItemRisksId: activeDealership.id,
          },
        });
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (previousHelpNavigationPath) {
      previousLocation.current = previousHelpNavigationPath;
      setPreviousPath(null);
    }
    void createNavigationEvent({
      variables: {
        input: {
          toPathname: pathname,
          fromPathname: previousLocation.current,
        },
      },
    });
    if (pathname !== previousLocation.current) {
      previousLocation.current = pathname;
    }
  }, [pathname]);

  const navigate = useNavigate();
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${UNAUTHENTICATED_PATHS.Login}?redirect=${pathname}${search}`, { replace: true });
    }
  }, [isAuthenticated]);
  return <Outlet />;
}
